import React, { useLayoutEffect } from 'react';
import { type Href, useNavigation } from 'expo-router';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { Aside } from '@expo/html-elements';

import { Container, theme, useNavbarContainerHeight, useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsLoggedIn, useIsLoggingIn } from '../../../context/AuthMachine';
import { LoadingAnimation } from '../../LoadingAnimation';
import { PageWrapper } from '../../PageWrapper';
import { AccountMenu } from '../AccountMenu';
import { AccountSignedOutMessage } from '../AccountSignedOutMessage';

import type { AccountTabScreenId } from '../Account.types';

// ────────────────────────────────────────────────────────────────────────────────

export const AccountScreenContainer = (props: AccountScreenContainerProps) => {
  const { headerShown, redirectTo, screen: screenId } = props;

  const { currentBreakpoint } = useResponsive();
  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();
  const navigation = useNavigation();
  const setOptions = navigation.setOptions;

  const isAccountUpdatesEnabled = useFeatureFlag('CELS-5485-order-account-updates-enabled');

  // ─── Derived ───────────────────────────────────────────────────────────────

  const shouldShowHeader = headerShown ?? !isAccountUpdatesEnabled;

  // ─── Effects ─────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    setOptions({ headerShown: shouldShowHeader });
  }, [setOptions, shouldShowHeader]);

  // ─────────────────────────────────────────────────────────────────

  if (isLoggingIn) {
    return <LoadingAnimation />;
  }

  if (!isLoggedIn) {
    return <AccountSignedOutMessage redirectTo={redirectTo ?? getAccountScreenHref(screenId)} />;
  }

  if (currentBreakpoint.isXS) {
    return <AccountScreenContainerXS {...props} />;
  }

  return <AccountScreenContainerSM {...props} />;
};

// ─── Sub Components ──────────────────────────────────────────────────────────

const AccountScreenContainerPageWrapper = (
  props: Pick<AccountScreenContainerProps, 'children' | 'testID'>,
) => {
  const { testID, children } = props;
  const { currentBreakpoint } = useResponsive();

  return (
    <PageWrapper
      testID={testID}
      showsVerticalScrollIndicator={false}
      style={styles.pageWrapper}
      withHeaderTitle
      withoutFooter={currentBreakpoint.isXS}
    >
      {children}
    </PageWrapper>
  );
};

const AccountScreenContainerSM = (props: AccountScreenContainerProps) => {
  const { screen: screenId, testID, children, dataSet } = props;

  const { minWidth, match } = useResponsive();

  const { height: navbarHeight, paddingTopStyle: navbarPaddingTopStyle } =
    useNavbarContainerHeight();

  const tabContentNavbarPaddingStyle = useStyle(
    () => ({ paddingTop: navbarHeight + theme.spacing['12'] }),
    [navbarHeight],
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');

  // ─── Styles ──────────────────────────────────────────────────────────

  const menuWrapperStyles = [
    styles.menuWrapper,
    match([{}, styles.menuWrapperSM, styles.menuWrapperMD]),
    isNavbarV2Enabled ? navbarPaddingTopStyle : undefined,
  ];

  const tabContentStyles = [
    styles.tabContent,
    minWidth.isLG ? styles.tabContentLG : {},
    isNavbarV2Enabled ? tabContentNavbarPaddingStyle : undefined,
  ];

  return (
    <AccountScreenContainerPageWrapper testID={testID}>
      <Container customSpacing={0} wrapperStyle={styles.containerSM} style={styles.containerSM}>
        <View style={styles.contentWrapper}>
          <Aside style={menuWrapperStyles}>
            <AccountMenu screen={screenId} />
          </Aside>

          <View style={tabContentStyles} {...(dataSet ? { dataSet } : {})}>
            {children}
          </View>
        </View>
      </Container>
    </AccountScreenContainerPageWrapper>
  );
};

const AccountScreenContainerXS = (props: AccountScreenContainerProps) => {
  const { testID, children, removeSpacing, dataSet, screen } = props;

  const { paddingBottomStyle: navbarPaddingBottomStyle } = useNavbarContainerHeight();
  const { top: safeAreaTop } = useSafeAreaInsets();

  // ─── Flags ───────────────────────────────────────────────────────────

  const isAccountUpdatesEnabled = useFeatureFlag('CELS-5485-order-account-updates-enabled');
  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerDynamicStyles = useStyle(() => {
    if (!isAccountUpdatesEnabled || screen === 'orders') return;

    return {
      paddingTop: Math.max(safeAreaTop + theme.spacing['4'], theme.spacing['6']),
    };
  }, [isAccountUpdatesEnabled, safeAreaTop, screen]);

  const containerStyles = [
    styles.containerXS,
    isAccountUpdatesEnabled ? containerDynamicStyles : undefined,
    isNavbarV2Enabled ? navbarPaddingBottomStyle : undefined,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <AccountScreenContainerPageWrapper testID={testID}>
      <Container
        removeSpacing={removeSpacing}
        wrapperStyle={styles.containerXS}
        style={containerStyles}
        {...(dataSet ? { dataSet } : {})}
      >
        {children}
      </Container>
    </AccountScreenContainerPageWrapper>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pageWrapper: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  contentWrapper: {
    flex: 1,
    flexDirection: 'row',
  },
  containerXS: {
    flex: 1,
    minHeight: '100%',
  },
  containerSM: {
    flex: 1,
    minHeight: 700,
  },
  tabContent: {
    flex: 1,
    minHeight: '100%',
    paddingTop: theme.spacing['12'],
    paddingHorizontal: theme.spacing['10'],
  },
  tabContentLG: {
    paddingHorizontal: theme.spacing['20'],
  },
  menuWrapper: {
    borderRightWidth: 1,
    borderRightColor: theme.colors.LIGHT,
  },
  menuWrapperSM: {
    width: 280,
  },
  menuWrapperMD: {
    width: 320,
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getAccountScreenHref(screenId: AccountTabScreenId) {
  // The "profile" tab id maps to the `/account/info` route; everything else
  // matches its tab id 1:1.
  if (screenId === 'profile') return '/account/info';

  return `/account/${screenId}`;
}

// ────────────────────────────────────────────────────────────────────────────────

export type AccountScreenContainerProps = Readonly<{
  screen: AccountTabScreenId;
  headerShown?: boolean;
  removeSpacing?: boolean;
  dataSet?: Record<string, string>;
  redirectTo?: Href | string;
}> &
  ViewProps;
